import { useEffect, useState } from "react";
import { T } from "@v0y4ge/jrni-ui";
import {
  whoami, listTeamMembers, getDemoPersonas, demoSignIn, getAccountDetails,
  switchWorkspace as switchWorkspaceOp,
  inviteTeamMember, updateTeamMemberRole, resendTeamInvite, removeTeamMember as removeTeamMemberOp,
} from "@v0y4ge/api-client";
import { refreshTranslationCatalog, t as i18nT } from "../lib/i18n";
import { toast } from "./toast";

/**
 * Capability model — the stable surface the route guard + every screen's action
 * gates call: `can(perm)`, `useUser()`.
 *
 * This revision implements the role architecture from "Event Roles & Access":
 *  • Five Events roles (Event Creator / Coordinator / Manager / Approver / System Administrator).
 *  • Capabilities are the contract — features check `can("area.verb")`, never a role name.
 *  • Roles are PROCESS-SCOPED (events is built; queues / appointments come later and get
 *    their own role sets). A role belongs to a process; a user is assigned a role per process.
 *  • Role default capability sets are EDITABLE at runtime by an admin (persisted) — the
 *    Team & roles screen writes them.
 *  • Per-user access is ADDITIVE on top of the role: effective = roleDefaults ∪ grants − revokes.
 *    This is what lets an admin give one Coordinator `guests.import` without cloning the role.
 *
 * Data-layer note (for Claude Code): role edits, per-user grant/revoke, and the member list
 * persist to localStorage here so the design renders + demos statelessly. Wiring these to the
 * backend (saveRole, saveMemberAccess, listTeamMembers) is the data-layer's job; the UI already
 * calls the functions below.
 */

/* ─── Processes (role domains) ─────────────────────────────────────────
 * Events is built. Workforce is built (2026-07-16 scaffold — spec:
 * specs/workforce-management-spec-2026-07-16.md). Queues + Appointments are
 * placeholders so the admin UI can show the dimension and the model is ready
 * when those surfaces land. */
export type ProcessId = "events" | "queues" | "appointments" | "workforce" | "storeops";
interface LocalizedLabel { readonly label: string; labelKey: string; }
export interface ProcessDef extends LocalizedLabel { id: ProcessId; built: boolean; blurb: string; }
export const PROCESSES: ProcessDef[] = [
  { id: "events",       labelKey: "shell.access.events", get label() { return i18nT("shell.access.events"); },       built: true,  blurb: "Create, approve, run and report on events" },
  { id: "queues",       labelKey: "shell.access.queues", get label() { return i18nT("shell.access.queues"); },       built: true,  blurb: "Walk-in queues: joining, serving, messaging, health and regional insight" },
  { id: "workforce",    labelKey: "shell.access.workforce", get label() { return i18nT("shell.access.workforce"); },    built: true,  blurb: "Schedules, demand and day-of staffing across events, appointments and queues" },
  { id: "storeops",     labelKey: "shell.access.storeOps", get label() { return i18nT("shell.access.storeOps"); },    built: true,  blurb: "Luxury-retail store operations — floor, clienteling, rosters and targets" },
  { id: "appointments", labelKey: "shell.access.appointments", get label() { return i18nT("shell.access.appointments"); }, built: true, blurb: "1:1 and group appointments — booking, day book, comms and reporting" },
];

/* ─── Capability catalog (grouped, process-scoped) ─────────────────────
 * The labelled, grouped, risk-flagged list the role/permission editor renders.
 * Adding a feature = add a cap here, assign it to roles, gate it with can(). */
export interface Cap extends LocalizedLabel { id: string; risk?: boolean; isNew?: boolean; }
export interface CapGroup extends LocalizedLabel { id: string; process: ProcessId; caps: Cap[]; }

export const PERM_CATALOG: CapGroup[] = [
  { id: "events", labelKey: "shell.access.events", get label() { return i18nT("shell.access.events"); }, process: "events", caps: [
    { id: "events.read",           labelKey: "shell.access.viewEvents", get label() { return i18nT("shell.access.viewEvents"); } },
    { id: "events.write",          labelKey: "shell.access.createEditEvents", get label() { return i18nT("shell.access.createEditEvents"); } },
    { id: "events.discard_draft",  labelKey: "shell.access.discardOwnDraft", get label() { return i18nT("shell.access.discardOwnDraft"); }, isNew: true },
    { id: "events.unpublish",      labelKey: "shell.access.takeALiveEventOffline", get label() { return i18nT("shell.access.takeALiveEventOffline"); }, risk: true, isNew: true },
    { id: "events.approve",        labelKey: "shell.access.approveRejectSubmittedEvents", get label() { return i18nT("shell.access.approveRejectSubmittedEvents"); }, isNew: true },
    { id: "events.delete",         labelKey: "shell.access.cancelDeleteALiveEvent", get label() { return i18nT("shell.access.cancelDeleteALiveEvent"); }, risk: true },
  ]},
  { id: "guests", labelKey: "shell.access.guests", get label() { return i18nT("shell.access.guests"); }, process: "events", caps: [
    { id: "guests.read",    labelKey: "shell.access.viewGuestListPii", get label() { return i18nT("shell.access.viewGuestListPii"); } },
    { id: "guests.write",   labelKey: "shell.access.addEditSegmentGuests", get label() { return i18nT("shell.access.addEditSegmentGuests"); } },
    { id: "guests.import",  labelKey: "shell.access.bulkImportAGuestListCsv", get label() { return i18nT("shell.access.bulkImportAGuestListCsv"); }, risk: true, isNew: true },
    { id: "guests.checkin", labelKey: "shell.access.dayOfCheckIn", get label() { return i18nT("shell.access.dayOfCheckIn"); } },
  ]},
  { id: "campaigns", labelKey: "shell.access.campaignsComms", get label() { return i18nT("shell.access.campaignsComms"); }, process: "events", caps: [
    { id: "campaigns.read",            labelKey: "shell.access.viewDraftsHistory", get label() { return i18nT("shell.access.viewDraftsHistory"); } },
    { id: "campaigns.write",           labelKey: "shell.access.draftEditComms", get label() { return i18nT("shell.access.draftEditComms"); } },
    { id: "campaigns.send",            labelKey: "shell.access.sendScheduleToGuests", get label() { return i18nT("shell.access.sendScheduleToGuests"); }, risk: true },
    { id: "campaigns.send_individual", labelKey: "shell.access.messageANamedIndividual", get label() { return i18nT("shell.access.messageANamedIndividual"); }, risk: true },
    { id: "campaigns.approve",         labelKey: "shell.access.approveRegulatedComms", get label() { return i18nT("shell.access.approveRegulatedComms"); } },
  ]},
  { id: "flows", labelKey: "shell.access.messagingFlows", get label() { return i18nT("shell.access.messagingFlows"); }, process: "events", caps: [
    { id: "flows.read",  labelKey: "shell.access.viewFlows", get label() { return i18nT("shell.access.viewFlows"); } },
    { id: "flows.write", labelKey: "shell.access.buildEditFlows", get label() { return i18nT("shell.access.buildEditFlows"); } },
  ]},
  { id: "questions", labelKey: "shell.access.bookingQuestions", get label() { return i18nT("shell.access.bookingQuestions"); }, process: "events", caps: [
    { id: "questions.read",  labelKey: "shell.access.viewQuestions", get label() { return i18nT("shell.access.viewQuestions"); } },
    { id: "questions.write", labelKey: "shell.access.createEditQuestions", get label() { return i18nT("shell.access.createEditQuestions"); } },
  ]},
  { id: "promos", labelKey: "shell.access.couponsPromos", get label() { return i18nT("shell.access.couponsPromos"); }, process: "events", caps: [
    { id: "promos.read",  labelKey: "shell.access.viewCouponsPromos", get label() { return i18nT("shell.access.viewCouponsPromos"); } },
    { id: "promos.write", labelKey: "shell.access.createEditCouponsPromos", get label() { return i18nT("shell.access.createEditCouponsPromos"); } },
  ]},
  { id: "surveys", labelKey: "shell.access.surveys", get label() { return i18nT("shell.access.surveys"); }, process: "events", caps: [
    { id: "surveys.read",  labelKey: "shell.access.viewSurveysSurveyQuestions", get label() { return i18nT("shell.access.viewSurveysSurveyQuestions"); }, isNew: true },
    { id: "surveys.write", labelKey: "shell.access.createEditSurveys", get label() { return i18nT("shell.access.createEditSurveys"); }, isNew: true },
  ]},
  { id: "services", labelKey: "shell.access.services", get label() { return i18nT("shell.access.services"); }, process: "events", caps: [
    { id: "services.read",  labelKey: "shell.access.viewServicesCategoriesPackages", get label() { return i18nT("shell.access.viewServicesCategoriesPackages"); }, isNew: true },
    { id: "services.write", labelKey: "shell.access.createEditServices", get label() { return i18nT("shell.access.createEditServices"); }, isNew: true },
  ]},
  { id: "products", labelKey: "shell.access.products", get label() { return i18nT("shell.access.products"); }, process: "events", caps: [
    { id: "products.read",  labelKey: "shell.access.viewTheProductCatalog", get label() { return i18nT("shell.access.viewTheProductCatalog"); }, isNew: true },
    { id: "products.write", labelKey: "shell.access.manageProductsTheirAssociations", get label() { return i18nT("shell.access.manageProductsTheirAssociations"); }, risk: true, isNew: true },
  ]},
  { id: "onsite", labelKey: "shell.access.onSiteOperations", get label() { return i18nT("shell.access.onSiteOperations"); }, process: "events", caps: [
    { id: "onsite.walkup",         labelKey: "shell.access.addWalkIns1s", get label() { return i18nT("shell.access.addWalkIns1s"); }, isNew: true },
    { id: "onsite.queue",          labelKey: "shell.access.manageWalkInQueues", get label() { return i18nT("shell.access.manageWalkInQueues"); }, isNew: true },
    { id: "onsite.occupancy",      labelKey: "shell.access.monitorLiveOccupancy", get label() { return i18nT("shell.access.monitorLiveOccupancy"); }, isNew: true },
    { id: "onsite.apps.run",       labelKey: "shell.access.runOnSiteApps", get label() { return i18nT("shell.access.runOnSiteApps"); }, isNew: true },
    { id: "onsite.apps.configure", labelKey: "shell.access.configureWhichAppsStaffRun", get label() { return i18nT("shell.access.configureWhichAppsStaffRun"); }, risk: true, isNew: true },
  ]},
  { id: "reports", labelKey: "shell.access.reports", get label() { return i18nT("shell.access.reports"); }, process: "events", caps: [
    { id: "reports.read",   labelKey: "shell.access.viewDashboards", get label() { return i18nT("shell.access.viewDashboards"); } },
    { id: "reports.export", labelKey: "shell.access.csvPdfExport", get label() { return i18nT("shell.access.csvPdfExport"); }, risk: true },
  ]},
  { id: "inbox", labelKey: "shell.access.inbox", get label() { return i18nT("shell.access.inbox"); }, process: "events", caps: [
    { id: "inbox.read",  labelKey: "shell.access.readReplies", get label() { return i18nT("shell.access.readReplies"); } },
    { id: "inbox.write", labelKey: "shell.access.replyToGuests", get label() { return i18nT("shell.access.replyToGuests"); }, risk: true },
  ]},
  { id: "compliance", labelKey: "shell.access.compliance", get label() { return i18nT("shell.access.compliance"); }, process: "events", caps: [
    { id: "compliance.read",  labelKey: "shell.access.viewConsentCompliance", get label() { return i18nT("shell.access.viewConsentCompliance"); } },
    { id: "compliance.write", labelKey: "shell.access.configureCompliance", get label() { return i18nT("shell.access.configureCompliance"); }, risk: true },
  ]},
  { id: "journeys", labelKey: "shell.access.journeys", get label() { return i18nT("shell.access.journeys", "Booking journeys"); }, process: "events", caps: [
    { id: "journeys.read",    labelKey: "shell.access.viewJourneys", get label() { return i18nT("shell.access.viewJourneys", "View booking journeys"); }, isNew: true },
    { id: "journeys.write",   labelKey: "shell.access.editJourneys", get label() { return i18nT("shell.access.editJourneys", "Create & edit journeys"); }, isNew: true },
    { id: "journeys.publish", labelKey: "shell.access.publishJourneys", get label() { return i18nT("shell.access.publishJourneys", "Publish journeys"); }, risk: true, isNew: true },
  ]},
  { id: "admin", labelKey: "shell.access.administration", get label() { return i18nT("shell.access.administration"); }, process: "events", caps: [
    { id: "settings.read",       labelKey: "shell.access.viewSettings", get label() { return i18nT("shell.access.viewSettings"); } },
    { id: "settings.write",      labelKey: "shell.access.editSettings", get label() { return i18nT("shell.access.editSettings"); }, risk: true },
    { id: "settings.factory_reset", labelKey: "shell.access.factoryResetTenantToSeeds", get label() { return i18nT("shell.access.factoryResetTenantToSeeds"); }, risk: true },
    { id: "team.read",           labelKey: "shell.access.viewTeamRoles", get label() { return i18nT("shell.access.viewTeamRoles"); } },
    { id: "team.write",          labelKey: "shell.access.manageTeamRoles", get label() { return i18nT("shell.access.manageTeamRoles"); }, risk: true },
    { id: "branding.read",       labelKey: "shell.access.viewBranding", get label() { return i18nT("shell.access.viewBranding"); } },
    { id: "branding.write",      labelKey: "shell.access.editBranding", get label() { return i18nT("shell.access.editBranding"); } },
    { id: "locations.read",      labelKey: "shell.access.viewLocations", get label() { return i18nT("shell.access.viewLocations"); } },
    { id: "locations.write",     labelKey: "shell.access.manageLocations", get label() { return i18nT("shell.access.manageLocations"); }, risk: true },
    { id: "billing.read",        labelKey: "shell.access.viewBilling", get label() { return i18nT("shell.access.viewBilling"); } },
    { id: "billing.write",       labelKey: "shell.access.manageBilling", get label() { return i18nT("shell.access.manageBilling"); }, risk: true },
    { id: "integrations.read",   labelKey: "shell.access.viewIntegrations", get label() { return i18nT("shell.access.viewIntegrations"); } },
    { id: "integrations.write",  labelKey: "shell.access.manageIntegrations", get label() { return i18nT("shell.access.manageIntegrations"); }, risk: true },
    { id: "audit.read",          labelKey: "shell.access.viewAuditLog", get label() { return i18nT("shell.access.viewAuditLog"); }, risk: true },
    { id: "audit.read_object",   labelKey: "shell.access.viewAnItemSChangeHistory", get label() { return i18nT("shell.access.viewAnItemSChangeHistory"); }, isNew: true },
    { id: "delivery.read",       labelKey: "shell.access.viewDeliveryLog", get label() { return i18nT("shell.access.viewDeliveryLog"); }, risk: true },
    { id: "observability.read",  labelKey: "shell.access.viewApiObservability", get label() { return i18nT("shell.access.viewApiObservability", "View API activity"); }, risk: true, isNew: true },
    /* Unified administrator, Phase 1 (2026-08-05, operator-directed). The flag
     * `isAdmin()` reads instead of comparing the role NAME. Held only by the
     * System Administrator role, whose perms are the whole catalog anyway — so
     * this is behaviour-neutral today and becomes load-bearing the moment the
     * role is renamed or a second tenant-scoped admin shape appears. Governance
     * affordances (audit trail, AI settings, ticket price-lock, branding audit)
     * all resolve through it. Catalog key pending — fallback carries the English. */
    { id: "admin.super",         labelKey: "shell.access.fullAdministrativeAuthority", get label() { return i18nT("shell.access.fullAdministrativeAuthority", "Full administrative authority"); }, risk: true, isNew: true },
  ]},
  /* ── Workforce process (2026-07-16) ── */
  { id: "wf-scheduling", labelKey: "shell.access.scheduling", get label() { return i18nT("shell.access.scheduling"); }, process: "workforce", caps: [
    { id: "schedules.read",    labelKey: "shell.access.viewSchedulesShifts", get label() { return i18nT("shell.access.viewSchedulesShifts"); }, isNew: true },
    { id: "schedules.write",   labelKey: "shell.access.createEditShifts", get label() { return i18nT("shell.access.createEditShifts"); }, isNew: true },
    { id: "schedules.publish", labelKey: "shell.access.publishSchedulesToStaff", get label() { return i18nT("shell.access.publishSchedulesToStaff"); }, risk: true, isNew: true },
    /* Stage 7 (2026-07-29): discarding a week's drafts throws away unpublished work
     * for a whole location, so it is separable from schedules.write. Catalog key
     * not yet shipped (kit-owned) — fallback carries the English (REQ-096i). */
    { id: "schedules.discard", labelKey: "shell.access.discardDraftShifts", get label() { return i18nT("shell.access.discardDraftShifts", "Discard draft shifts"); }, risk: true, isNew: true },
  ]},
  { id: "wf-demand", labelKey: "shell.access.demandForecasting", get label() { return i18nT("shell.access.demandForecasting"); }, process: "workforce", caps: [
    { id: "demand.read", labelKey: "shell.access.viewDemandForecastCoverage", get label() { return i18nT("shell.access.viewDemandForecastCoverage"); }, isNew: true },
  ]},
  { id: "wf-staffing", labelKey: "shell.access.eventStaffing", get label() { return i18nT("shell.access.eventStaffing"); }, process: "workforce", caps: [
    { id: "staffing.read",  labelKey: "shell.access.viewEventStaffingPlans", get label() { return i18nT("shell.access.viewEventStaffingPlans"); }, isNew: true },
    { id: "staffing.write", labelKey: "shell.access.editEventRolesAssignments", get label() { return i18nT("shell.access.editEventRolesAssignments"); }, isNew: true },
  ]},
  { id: "wf-ops", labelKey: "shell.access.dayOfOperations", get label() { return i18nT("shell.access.dayOfOperations"); }, process: "workforce", caps: [
    { id: "wfops.read", labelKey: "shell.access.viewTheLiveOpsBoard", get label() { return i18nT("shell.access.viewTheLiveOpsBoard"); }, isNew: true },
    { id: "wfops.write", labelKey: "shell.access.resolveFloorAlertsApplyReFlow", get label() { return i18nT("shell.access.resolveFloorAlertsApplyReFlow"); }, risk: true, isNew: true },
  ]},
  { id: "wf-requests", labelKey: "shell.access.swapsTimeOff", get label() { return i18nT("shell.access.swapsTimeOff"); }, process: "workforce", caps: [
    { id: "swaps.read",      labelKey: "shell.access.viewSwapTimeOffRequests", get label() { return i18nT("shell.access.viewSwapTimeOffRequests"); }, isNew: true },
    { id: "swaps.request",   labelKey: "shell.access.requestSwapsClaimOpenShifts", get label() { return i18nT("shell.access.requestSwapsClaimOpenShifts"); }, isNew: true },
    { id: "swaps.approve",   labelKey: "shell.access.approveSwapsTimeOff", get label() { return i18nT("shell.access.approveSwapsTimeOff"); }, risk: true, isNew: true },
    { id: "timeoff.request", labelKey: "shell.access.requestTimeOff", get label() { return i18nT("shell.access.requestTimeOff"); }, isNew: true },
  ]},
  { id: "wf-rules", labelKey: "shell.access.laborRulesCompliance", get label() { return i18nT("shell.access.laborRulesCompliance"); }, process: "workforce", caps: [
    { id: "laborrules.read",  labelKey: "shell.access.viewLaborRulesViolations", get label() { return i18nT("shell.access.viewLaborRulesViolations"); }, isNew: true },
    { id: "laborrules.write", labelKey: "shell.access.configureLaborRules", get label() { return i18nT("shell.access.configureLaborRules"); }, risk: true, isNew: true },
  ]},
  { id: "wf-self", labelKey: "shell.access.mySchedule", get label() { return i18nT("shell.access.mySchedule"); }, process: "workforce", caps: [
    { id: "myschedule.read", labelKey: "shell.access.viewOwnShiftsSchedule", get label() { return i18nT("shell.access.viewOwnShiftsSchedule"); }, isNew: true },
    { id: "outcomes.write",  labelKey: "shell.access.recordOwnAppointmentOutcomes", get label() { return i18nT("shell.access.recordOwnAppointmentOutcomes"); }, isNew: true },
    /* 2026-08-03 (REQ-126): the staff side of the coaching loop — acknowledge an
     * entry on your OWN thread, mark assigned training complete, add a reflection.
     * Never grants reading or writing anyone else's coaching (that stays
     * coaching.read / coaching.write on the manager side). */
    { id: "coaching.acknowledge_own", labelKey: "shell.access.acknowledgeOwnCoaching", get label() { return i18nT("shell.access.acknowledgeOwnCoaching"); }, isNew: true },
  ]},
  /* ── Workforce · Aperture Bank six-persona port P1 (2026-07-17). Read caps
   * for the pending-port surfaces (see shell/navProfiles.ts + BankPending):
   * enforcement = rail `needs` + ROUTE_READ_PERM; registry entries in
   * skills/role-capability-tracking (nav-only until each port lands). ── */
  { id: "wf-runs", labelKey: "shell.access.generateRunsCopilot", get label() { return i18nT("shell.access.generateRunsCopilot"); }, process: "workforce", caps: [
    { id: "runs.read",   labelKey: "shell.access.viewGenerateRunStatusOversight", get label() { return i18nT("shell.access.viewGenerateRunStatusOversight"); }, isNew: true },
    { id: "copilot.run", labelKey: "shell.access.useTheSchedulingCopilot", get label() { return i18nT("shell.access.useTheSchedulingCopilot"); }, isNew: true },
  ]},
  { id: "wf-people", labelKey: "shell.access.peoplePlanning", get label() { return i18nT("shell.access.peoplePlanning"); }, process: "workforce", caps: [
    { id: "advisors.read",     labelKey: "shell.access.viewTheAdvisorRosterPerformance", get label() { return i18nT("shell.access.viewTheAdvisorRosterPerformance"); }, isNew: true },
    { id: "coaching.read",     labelKey: "shell.access.viewCoachingPerformanceInsights", get label() { return i18nT("shell.access.viewCoachingPerformanceInsights"); }, isNew: true },
    { id: "assists.read",      labelKey: "shell.access.viewManagerAssistAnalytics", get label() { return i18nT("shell.access.viewManagerAssistAnalytics"); }, isNew: true },
    { id: "requisitions.read", labelKey: "shell.access.viewRequisitionsCandidatePipeline", get label() { return i18nT("shell.access.viewRequisitionsCandidatePipeline"); }, isNew: true },
    { id: "requisitions.write", labelKey: "shell.access.openRequisitionsAdvanceCandidates", get label() { return i18nT("shell.access.openRequisitionsAdvanceCandidates"); }, isNew: true },
    { id: "hrplanning.read",   labelKey: "shell.access.viewWorkforcePlanning", get label() { return i18nT("shell.access.viewWorkforcePlanning"); }, isNew: true },
  ]},
  { id: "wf-network", labelKey: "shell.access.networkBackOffice", get label() { return i18nT("shell.access.networkBackOffice"); }, process: "workforce", caps: [
    { id: "region.read",    labelKey: "shell.access.viewRegionalOversightSurfaces", get label() { return i18nT("shell.access.viewRegionalOversightSurfaces"); }, isNew: true },
    { id: "region.write",   labelKey: "shell.access.proposeSecondmentsRedistributeHeadcount", get label() { return i18nT("shell.access.proposeSecondmentsRedistributeHeadcount"); }, isNew: true },
    { id: "wfreports.read", labelKey: "shell.access.viewWorkforceReporting", get label() { return i18nT("shell.access.viewWorkforceReporting"); }, isNew: true },
    { id: "payroll.read",   labelKey: "shell.access.viewThePayrollBridge", get label() { return i18nT("shell.access.viewThePayrollBridge"); }, isNew: true },
    { id: "payroll.write",  labelKey: "shell.access.runPayrollReconciliation", get label() { return i18nT("shell.access.runPayrollReconciliation"); }, risk: true, isNew: true },
    { id: "marketplace.write", labelKey: "shell.access.configureTheShiftMarketplace", get label() { return i18nT("shell.access.configureTheShiftMarketplace"); }, isNew: true },
    { id: "setup.write",    labelKey: "shell.access.completeWorkforceSetupSteps", get label() { return i18nT("shell.access.completeWorkforceSetupSteps"); }, isNew: true },
  ]},
  /* ── Queues process (2026-07-17 — queuing first pass; ported 2026-07-23,
   * i18n keys pending — t(key, fallback) self-heals when the catalog lands) ── */
  { id: "q-setup", labelKey: "shell.access.queueSetup", get label() { return i18nT("shell.access.queueSetup", "Queue setup"); }, process: "queues", caps: [
    { id: "queues.read",    labelKey: "shell.access.viewQueuesLiveBoards", get label() { return i18nT("shell.access.viewQueuesLiveBoards", "View queues & live boards"); }, isNew: true },
    { id: "queues.write",   labelKey: "shell.access.createConfigureQueues", get label() { return i18nT("shell.access.createConfigureQueues", "Create & configure queues"); }, isNew: true },
    { id: "queues.publish", labelKey: "shell.access.openPauseCloseQueues", get label() { return i18nT("shell.access.openPauseCloseQueues", "Open / pause / close queues"); }, risk: true, isNew: true },
  ]},
  { id: "q-serve", labelKey: "shell.access.serving", get label() { return i18nT("shell.access.serving", "Serving"); }, process: "queues", caps: [
    { id: "queue.serve",         labelKey: "shell.access.callServeCompleteCustomers", get label() { return i18nT("shell.access.callServeCompleteCustomers", "Call, serve & complete customers"); }, isNew: true },
    { id: "queue.entries.write", labelKey: "shell.access.addEditMoveRemoveQueueEntries", get label() { return i18nT("shell.access.addEditMoveRemoveQueueEntries", "Add, edit, move & remove queue entries"); }, isNew: true },
    { id: "queue.message",       labelKey: "shell.access.twoWayMessageWithCustomersSmsWhatsapp", get label() { return i18nT("shell.access.twoWayMessageWithCustomersSmsWhatsapp", "2-way message with customers (SMS / WhatsApp)"); }, risk: true, isNew: true },
  ]},
  { id: "q-flows", labelKey: "shell.access.queueFlows", get label() { return i18nT("shell.access.queueFlows", "Queue flows"); }, process: "queues", caps: [
    { id: "queueflows.read",  labelKey: "shell.access.viewQueueFlows", get label() { return i18nT("shell.access.viewQueueFlows", "View queue flows"); }, isNew: true },
    { id: "queueflows.write", labelKey: "shell.access.designEditQueueFlows", get label() { return i18nT("shell.access.designEditQueueFlows", "Design & edit queue flows"); }, isNew: true },
  ]},
  { id: "q-insight", labelKey: "shell.access.healthInsight", get label() { return i18nT("shell.access.healthInsight", "Health & insight"); }, process: "queues", caps: [
    { id: "queuehealth.read",       labelKey: "shell.access.viewQueueHealthMetrics", get label() { return i18nT("shell.access.viewQueueHealthMetrics", "View queue health & metrics"); }, isNew: true },
    { id: "queuehealth.alerts",     labelKey: "shell.access.configureAlertsThresholds", get label() { return i18nT("shell.access.configureAlertsThresholds", "Configure alerts & thresholds"); }, risk: true, isNew: true },
    { id: "queuemetrics.export",    labelKey: "shell.access.exportQueueMetrics", get label() { return i18nT("shell.access.exportQueueMetrics", "Export queue metrics"); }, risk: true, isNew: true },
    { id: "queueinsight.recommend", labelKey: "shell.access.askTheSystemForRecommendations", get label() { return i18nT("shell.access.askTheSystemForRecommendations", "Ask the system for recommendations"); }, isNew: true },
  ]},
  /* ── Store Ops process (2026-07-16, Maison Rivière fidelity port — spec:
   * specs/maison-riviere-fidelity-matrix-2026-07-16.md) ── */
  { id: "so-core", labelKey: "shell.access.storeOperations", get label() { return i18nT("shell.access.storeOperations"); }, process: "storeops", caps: [
    { id: "storeops.read",      labelKey: "shell.access.viewStoreOpsSurfaces", get label() { return i18nT("shell.access.viewStoreOpsSurfaces"); }, isNew: true },
    { id: "storeops.manage",    labelKey: "shell.access.publishRostersManageStoreConfig", get label() { return i18nT("shell.access.publishRostersManageStoreConfig"); }, risk: true, isNew: true },
    { id: "storeops.floorplan", labelKey: "shell.access.planRostersSkillsAdvisorPerformance", get label() { return i18nT("shell.access.planRostersSkillsAdvisorPerformance"); }, isNew: true },
    { id: "storeops.region",    labelKey: "shell.access.viewTheCrossStoreRegion", get label() { return i18nT("shell.access.viewTheCrossStoreRegion"); }, isNew: true },
  ]},
  { id: "so-clienteling", labelKey: "shell.access.clienteling", get label() { return i18nT("shell.access.clienteling"); }, process: "storeops", caps: [
    { id: "clienteling.read",  labelKey: "shell.access.viewClientBookSessions", get label() { return i18nT("shell.access.viewClientBookSessions"); }, isNew: true },
    { id: "clienteling.write", labelKey: "shell.access.bookSessionsAssignMatchesLogNotes", get label() { return i18nT("shell.access.bookSessionsAssignMatchesLogNotes"); }, isNew: true },
  ]},
  { id: "so-self", labelKey: "shell.access.myBookAssociate", get label() { return i18nT("shell.access.myBookAssociate"); }, process: "storeops", caps: [
    { id: "mybook.read", labelKey: "shell.access.viewOwnDayBookTargetsSchedule", get label() { return i18nT("shell.access.viewOwnDayBookTargetsSchedule"); }, isNew: true },
  ]},
];

export function processCaps(process: ProcessId): string[] {
  return PERM_CATALOG.filter((g) => g.process === process).flatMap((g) => g.caps.map((c) => c.id));
}
const ALL_CAP_IDS = PERM_CATALOG.flatMap((g) => g.caps.map((c) => c.id));
/* APT-13 ASSIGNMENT BRIDGE (2026-07-26, auth-freeze compliant): the canonical
 * appointments capability family (15 appointments.* caps) + the two roles ship
 * with Claude Code's auth wave — CD does NOT author a PERM_CATALOG group for
 * them (frozen). These four coarse ids gate the new rail items / routes until
 * the canonical catalog lands, then gates re-point to canonical ids and this
 * const + the System Administrator concat below retire (REQ-089 tracks). */
export const APPT_BRIDGE_CAPS = ["appointments.read", "appointments.book", "appointments.checkin", "appointments.manage"];
const CAP_SET = new Set(ALL_CAP_IDS);

/* ─── Role definitions, per process ────────────────────────────────────
 * `perms` is the DEFAULT set; the live editable set lives in ROLE_STORE below. */
export type PillTone = "danger" | "info" | "warn" | "success" | "neutral" | "primary";
export interface RoleDef {
  id: string; process: ProcessId; readonly label: string; labelKey: string; tone: PillTone; color: string;
  blurb: string; perms: string[]; isNew?: boolean; system?: boolean;
}

const EVENTS_CREATOR = [
  "events.read", "events.write", "events.discard_draft",
  "guests.read", "guests.write", "guests.import",
  "campaigns.read", "campaigns.write",
  "flows.read", "flows.write",
  "questions.read", "questions.write",
  "promos.read", "promos.write",
  "surveys.read", "surveys.write",
  // services.read/write DROPPED (Creator role review, 2026-07-10, ingested
  // 2026-07-13): Services is admin-owned setup — the more-powerful Event
  // Manager was already scoped out of it (2026-07-08), so the narrower Creator
  // holding it was inconsistent. Rail hides via the missing cap;
  // NAV_HIDDEN_BY_ROLE["Event Creator"] is belt-and-braces below.
  "reports.read",
];
// Event Coordinator = pure on-site operations, with NO configuration surfaces
// (roles review, 2026-07-08). Scoped to running the room on the day: check-in,
// walk-ups, queues and live occupancy. Deliberately EXCLUDES all Configuration
// items — Messaging (campaigns.*), Customers (guests.read/write), Surveys,
// Services, Locations and Reports were removed so the Coordinator has no config
// menu at all (see NAV_HIDDEN_BY_ROLE). `events.read` is retained so Event Day +
// Calendar context resolve; `guests.checkin` stays so day-of check-in works
// without exposing the Customers list.
const EVENTS_COORDINATOR = [
  "events.read",
  "guests.checkin",
  "onsite.walkup", "onsite.queue", "onsite.occupancy", "onsite.apps.run",
];
// Event Manager owns an event end-to-end — authoring + on-site + guests + comms
// + reports — but is scoped OUT of the setup/config surfaces an admin owns:
// Services (caps dropped here → Services rail hidden), Resources, the coming-soon
// Queues *process* and Appointments (hidden via NAV_HIDDEN_BY_ROLE). onsite.queue
// is RETAINED — the Event-Day walk-in queue tool stays part of day-of ops
// (operator decision, 2026-07-08). Manager holds NO governance caps, so Settings
// and the admin surfaces stay hidden. Coordinator is now narrow, so Manager
// re-adds the guests/comms/reports/locations/surveys caps explicitly.
const EVENTS_MANAGER_EXCLUDE = new Set(["services.read", "services.write"]);
// inbox.read/write + delivery.read added 2026-07-09 role-access review (ingested
// 2026-07-13): the Manager owns comms end-to-end — can broadcast, so must also be
// able to read two-way replies (Inbox) and audit the delivery/send log.
const EVENTS_MANAGER = [...new Set([...EVENTS_CREATOR, ...EVENTS_COORDINATOR, "guests.read", "guests.write", "campaigns.read", "campaigns.send", "campaigns.send_individual", "inbox.read", "inbox.write", "delivery.read", "reports.read", "reports.export", "locations.read", "surveys.read", "audit.read_object", "observability.read"])].filter((p) => !EVENTS_MANAGER_EXCLUDE.has(p));
const EVENTS_APPROVER = [
  "events.read", "events.unpublish", "events.approve",
  "guests.read",
  "campaigns.read", "campaigns.approve",
  "flows.read", "questions.read", "promos.read", "surveys.read", "services.read",
  // reports.export granted 2026-07-09 role-access review (ingested 2026-07-13):
  // the sign-off persona can pull the CSV/PDF audit trail behind a decision.
  "reports.read", "reports.export",
  "compliance.read",
];
// Operations Admin (2026-07-28) — the least-privilege owner of customer journeys
// and the service catalog across the branch network. Deliberately NOT derived
// from System Administrator or any events bundle: this persona must not reach
// Events, Tickets, Calendar, comms or governance. The set below is exactly the
// capability bundle the backend already issues for this role, so cd-app's
// role-fallback resolution and the server's canonical snapshot agree.
const EVENTS_OPERATIONS_ADMIN = [
  "journeys.read", "journeys.write", "journeys.publish",
  "services.read", "services.write",
  "locations.read",
  "observability.read",
];

/* Workforce role default sets (2026-07-16). Location scoping (Venue Manager →
 * own locations; Team Member → published-only reads) is server-side. */
const WF_ALL = [
  "schedules.read", "schedules.write", "schedules.publish", "schedules.discard", "demand.read",
  "staffing.read", "staffing.write", "wfops.read",
  "swaps.read", "swaps.request", "swaps.approve", "timeoff.request",
  "laborrules.read", "laborrules.write", "myschedule.read",
];
const WF_SCHEDULER = [...WF_ALL];
const WF_VENUE_MANAGER = WF_ALL.filter((p) => p !== "laborrules.write");
const WF_OPS_LEAD = ["schedules.read", "staffing.read", "wfops.read", "swaps.read", "swaps.request", "myschedule.read"];
const WF_TEAM_MEMBER = ["myschedule.read", "schedules.read", "swaps.request", "timeoff.request"];
const WF_REGIONAL = ["schedules.read", "demand.read", "staffing.read", "wfops.read", "swaps.read", "laborrules.read"];

/* Aperture Bank role configurations (six-persona port P1, 2026-07-17 —
 * personas verbatim from the WFM exploration workspace; per-role rail lives
 * in shell/navProfiles.ts, plan in "Aperture Bank Persona Audit & Plan"). */
const WF_BANK_ADMIN = [...WF_ALL, "copilot.run", "runs.read", "wfreports.read", "payroll.read", "payroll.write", "advisors.read", "marketplace.write", "setup.write"];
const WF_HRBP = ["hrplanning.read", "advisors.read", "coaching.read", "assists.read", "requisitions.read", "requisitions.write"];
/* Day-of operations added 2026-08-10 (operator): the branch manager runs her
 * branch's floor, so Event Day and the Concierge are hers too — the persona
 * blurb has promised both since P2, and the Floor Calendar's "Open event day" /
 * "Open queue" jump-offs land on their route guards. events.read is the minimum
 * for Event Day context to resolve (same reasoning as EVENTS_COORDINATOR). */
const WF_BRANCH_MANAGER = [...WF_VENUE_MANAGER, "copilot.run", "advisors.read", "coaching.read", "wfops.write",
  "events.read", "guests.checkin", "onsite.walkup", "onsite.queue", "queue.serve"];
const WF_REGIONAL_BANK = [...WF_REGIONAL, "region.read", "region.write", "runs.read", "coaching.read", "requisitions.read"];
const WF_PERSONAL_BANKER = ["myschedule.read", "schedules.read", "swaps.request", "timeoff.request", "outcomes.write", "coaching.acknowledge_own", "observability.read"];

/* Store Ops role default sets (2026-07-16). Store scoping is server-side. */
/* Same day-of grant for the store manager (2026-08-10, operator — "branch
 * managers AND store managers"): their rail is the default grouped one, so the
 * Event Day and Concierge items appear via these caps alone. */
const SO_STORE_MANAGER = ["storeops.read", "storeops.manage", "storeops.floorplan", "clienteling.read", "clienteling.write",
  "events.read", "guests.checkin", "onsite.walkup", "onsite.queue", "queue.serve"];
const SO_REGIONAL = ["storeops.read", "storeops.region", "storeops.manage", "clienteling.read", "clienteling.write"];
const SO_ASSOCIATE = ["mybook.read", "clienteling.read", "clienteling.write"];

export const ROLE_CATALOG: Record<ProcessId, RoleDef[]> = {
  events: [
    { id: "Event Creator",        process: "events", labelKey: "shell.access.eventCreator", get label() { return i18nT("shell.access.eventCreator"); },        tone: "info",    color: "#2D60EB", blurb: "Authors events — builds, submits for approval, publishes, and can discard own drafts.", perms: EVENTS_CREATOR },
    { id: "Event Coordinator",    process: "events", labelKey: "shell.access.eventCoordinator", get label() { return i18nT("shell.access.eventCoordinator"); },    tone: "warn",    color: "#C77F14", blurb: "Runs events on-site: check-in, walk-ups, queues and live occupancy. No configuration access.", perms: EVENTS_COORDINATOR },
    { id: "Event Manager",        process: "events", labelKey: "shell.access.eventManager", get label() { return i18nT("shell.access.eventManager"); },        tone: "info",    color: "#0E7490", blurb: "Owns an event end-to-end — authoring, guests, comms, on-site day-of ops and reporting. No setup/config surfaces (Services, Resources, Queues, Appointments).", perms: EVENTS_MANAGER, isNew: true },
    { id: "Event Approver",       process: "events", labelKey: "shell.access.eventApprover", get label() { return i18nT("shell.access.eventApprover"); },       tone: "success", color: "#027D47", blurb: "Reviews & signs off submissions (approve / request changes / reject) and can take a live event offline. Read-only on content — no authoring, sending or publishing.", perms: EVENTS_APPROVER },
    // Perms corrected (2026-07-28) to the canonical Operations Admin bundle: this
    // role must NOT hold events.read (that opens Events/Tickets/Calendar) — it
    // owns journeys.* + services.* + locations.read only, matching the exact
    // bundle the backend issues for events.operations_admin.
    { id: "Operations Admin",     process: "events", labelKey: "shell.access.operationsAdmin", get label() { return i18nT("shell.access.operationsAdmin", "Operations Admin"); },     tone: "info",    color: "#0F766E", blurb: "Maintains customer journeys and the service catalog across the branch network. No events, comms or governance access.", perms: EVENTS_OPERATIONS_ADMIN, isNew: true },
    { id: "System Administrator", process: "events", labelKey: "shell.access.systemAdministrator", get label() { return i18nT("shell.access.systemAdministrator"); }, tone: "primary", color: "#5142C7", blurb: "Full access — governance, settings, approvals config, all data.", perms: [...ALL_CAP_IDS, ...APPT_BRIDGE_CAPS], system: true },
  ],
  workforce: [
    { id: "Workforce Scheduler", process: "workforce", labelKey: "shell.access.workforceScheduler", get label() { return i18nT("shell.access.workforceScheduler"); }, tone: "primary", color: "#5142C7", blurb: "Owns the staffing plan across locations — builds and publishes schedules, tunes rules, decides requests.", perms: WF_SCHEDULER, isNew: true },
    { id: "Venue Manager",       process: "workforce", labelKey: "shell.access.venueManager", get label() { return i18nT("shell.access.venueManager"); },       tone: "info",    color: "#0E7490", blurb: "Runs staffing for their location(s): schedules, event staffing, day-of board, swaps and time off.", perms: WF_VENUE_MANAGER, isNew: true },
    { id: "Ops Lead",            process: "workforce", labelKey: "shell.access.opsLead", get label() { return i18nT("shell.access.opsLead"); },            tone: "warn",    color: "#C77F14", blurb: "Day-of floor lead — live ops board, schedule context and event staffing readiness. No publishing.", perms: WF_OPS_LEAD, isNew: true },
    { id: "Team Member",         process: "workforce", labelKey: "shell.access.teamMember", get label() { return i18nT("shell.access.teamMember"); },         tone: "success", color: "#027D47", blurb: "Self-service: own shifts, claiming open shifts, swaps and time off. Sees only published schedules.", perms: WF_TEAM_MEMBER, isNew: true },
    { id: "Regional Lead",       process: "workforce", labelKey: "shell.access.regionalLead", get label() { return i18nT("shell.access.regionalLead"); },       tone: "info",    color: "#2D60EB", blurb: "Read-across every location: demand, coverage, schedules, requests and compliance posture.", perms: WF_REGIONAL, isNew: true },
    // Aperture Bank six-persona port (P1, 2026-07-17) — role configurations behind the bank's per-persona navigation.
    { id: "Workforce Admin",         process: "workforce", labelKey: "shell.access.workforceAdmin", get label() { return i18nT("shell.access.workforceAdmin"); },         tone: "primary", color: "#5142C7", blurb: "Owns the whole workforce platform — schedule, copilot, Generate runs, rules, requests, reporting and the payroll bridge.", perms: WF_BANK_ADMIN, isNew: true },
    { id: "HR Business Partner",     process: "workforce", labelKey: "shell.access.hrBusinessPartner", get label() { return i18nT("shell.access.hrBusinessPartner"); },     tone: "info",    color: "#C13584", blurb: "Workforce planning, advisors, coaching, Manager Assist and requisitions — authors, never publishes.", perms: WF_HRBP, isNew: true },
    { id: "Branch Manager",          process: "workforce", labelKey: "shell.access.branchManager", get label() { return i18nT("shell.access.branchManager"); },          tone: "info",    color: "#0E7490", blurb: "One branch end-to-end: floor, schedule, copilot, draft review, forecasts, requests and coaching.", perms: WF_BRANCH_MANAGER, isNew: true },
    { id: "Regional Lead (Banking)", process: "workforce", labelKey: "shell.access.regionalLeadBanking", get label() { return i18nT("shell.access.regionalLeadBanking"); }, tone: "info",    color: "#2D60EB", blurb: "District read-across with approval posture: regional overview, peer comparison, redistribution and run oversight.", perms: WF_REGIONAL_BANK, isNew: true },
    { id: "Personal Banker",         process: "workforce", labelKey: "shell.access.personalBanker", get label() { return i18nT("shell.access.personalBanker"); },         tone: "success", color: "#027D47", blurb: "Self-service front line: My Day, My Performance and My Time.", perms: WF_PERSONAL_BANKER, isNew: true },
  ],
  queues: [
    // Queuing (2026-07-17, restructured same day around administrative queue
    // creation & management — section tabs, not role-specific surfaces).
    // Queue Manager is the default full owner; where queues are locked down an
    // admin narrows the Manager role's live set (Team & Roles) and the Queue
    // Administrator keeps the ceiling. Queue TYPES are development-defined —
    // customers are never exposed to type management (operator, 2026-07-17;
    // queuetypes.manage removed). The former Regional Queue Manager role + Region surface were REMOVED (2026-07-17
    // operator decision) — superseded by the cross-project Reporting feature.
    { id: "Queue Staff",         process: "queues", labelKey: "shell.access.queueStaff", get label() { return i18nT("shell.access.queueStaff", "Queue Staff"); },         tone: "warn",    color: "#C77F14", blurb: "Works the queue at speed — call, serve, complete, move, add walk-ups and 2-way message customers. Any device.", perms: ["queues.read", "queue.serve", "queue.entries.write", "queue.message"], isNew: true },
    { id: "Queue Manager",       process: "queues", labelKey: "shell.access.queueManager", get label() { return i18nT("shell.access.queueManager", "Queue Manager"); },       tone: "info",    color: "#0E7490", blurb: "Owns queue health at their location — creates and configures queues, staffs them, designs journeys, watches metrics and resolves issues. May serve, but that's staff's focus.", perms: ["queues.read", "queues.write", "queues.publish", "queue.serve", "queue.entries.write", "queue.message", "queueflows.read", "queueflows.write", "queuehealth.read", "queuehealth.alerts", "queuemetrics.export", "queueinsight.recommend", "observability.read"], isNew: true },
  ],
  // APT-13 + auth-freeze (2026-07-26, Voyage packet §1): deliberately EMPTY.
  // The canonical roles (appointments.appointment_manager / _staff) + 15-cap
  // family ship with the auth wave — authoring a local role set here would be
  // deleted-on-arrival work. Until then the System Administrator bridge concat
  // (APPT_BRIDGE_CAPS) makes the process demoable; the Fork B1 assignment
  // recommendation lives in REQ-089 / the packet reply, seeded server-side.
  appointments: [],
  storeops: [
    { id: "Store Manager",          process: "storeops", labelKey: "shell.access.storeManager", get label() { return i18nT("shell.access.storeManager"); },          tone: "warn",    color: "#B08D57", blurb: "Runs the store end-to-end — floor, clienteling, rosters, skills, targets and in-store events.", perms: SO_STORE_MANAGER, isNew: true },
    { id: "Regional Store Manager", process: "storeops", labelKey: "shell.access.regionalStoreManager", get label() { return i18nT("shell.access.regionalStoreManager"); }, tone: "info",    color: "#8A6A3C", blurb: "The estate one level up — region overview, per-store attainment, clienteling and targets across 5 stores.", perms: SO_REGIONAL, isNew: true },
    { id: "Sales Associate",        process: "storeops", labelKey: "shell.access.salesAssociate", get label() { return i18nT("shell.access.salesAssociate"); },        tone: "success", color: "#B08D57", blurb: "The front line — my day, my client book, my targets and my schedule.", perms: SO_ASSOCIATE, isNew: true },
  ],
};

const ALL_ROLE_DEFS: RoleDef[] = Object.values(ROLE_CATALOG).flat();
function roleDefaultPerms(process: ProcessId, role: string): string[] {
  const def = (ROLE_CATALOG[process] || []).find((r) => r.id === role);
  return def ? def.perms : [];
}
export function getRoleDef(role: string, process: ProcessId = "events"): RoleDef | undefined {
  return (ROLE_CATALOG[process] || []).find((r) => r.id === role);
}
export function listRoles(process: ProcessId = "events"): RoleDef[] { return ROLE_CATALOG[process] || []; }
export function listRoleNames(process: ProcessId = "events"): string[] { return (ROLE_CATALOG[process] || []).map((r) => r.id); }

/* ─── Per-role navigation configuration (roles review, 2026-07-08) ──────
 * The authoritative list of rail ids a role should NOT see, independent of
 * capability coupling (some rail items share a read cap with a surface the role
 * keeps — e.g. events.read is shared by Events, Tickets, Calendar). Applied to
 * the desktop IconRail, the mobile drawer AND the launcher so the menu is
 * identical across experiences. Rail ids: see RAIL_ITEMS in chrome-rail.
 * NOTE: `tickets` is hidden for Coordinator + Approver here (this master has a
 * dedicated Tickets rail section that the upstream roles review predated). */
export const NAV_HIDDEN_BY_ROLE: Record<string, string[]> = {
  // Event Coordinator — on-site operations only, NO configuration menu. Hides
  // event setup, tickets, services, resources, appointments, queues, surveys,
  // locations, campaigns, messaging, customers/guests, reports AND the Dashboard
  // (analytics home leaning on reports data the Coordinator can't see). What
  // remains is Event Day (landing) + Calendar (read-only schedule context).
  "Event Coordinator": [
    "events", "tickets", "cs-queues", "cs-services",
    "resources", "surveys", "locations", "campaigns",
    "messaging", "guests", "reports", "dashboard",
  ],
  // Event Manager — owns the event lifecycle (incl. Tickets) but NOT the
  // setup/config surfaces: Services, Resources, and the coming-soon Queues +
  // Appointments processes. Locations stays visible (read-only) for context.
  "Event Manager": [
    "cs-services", "resources", "cs-queues",
  ],
  // Event Approver — review-only role that lives in the Approvals queue. Keeps
  // READ caps on every content type (to render the review detail), but the
  // standalone authoring/management surfaces are hidden to keep the menu
  // review-focused. Kept: Approvals (home), Events (browse + take-offline),
  // Reports, Campaigns (regulated-comms context), Dashboard, Calendar.
  "Event Approver": [
    "messaging", "tickets", "cs-services", "cs-questions", "surveys", "promos",
    "guests", "cs-queues",
  ],
  // Event Creator — curated menu (Creator role review, 2026-07-10, ingested
  // 2026-07-13). Hides the coming-soon Appointments + Queues process teasers
  // (gated on events.read, which every author holds — they leaked only here)
  // and Services (belt-and-braces: caps dropped above, so this holds even if an
  // admin re-grants services.read to a customised Creator). Everything the
  // Creator authors with (Events, Tickets, Campaigns, Messaging, Questions,
  // Surveys, Promos, Customers, Reports) stays visible.
  "Event Creator": [
    "cs-queues", "cs-services",
  ],
  // Workforce roles (2026-07-16): Team Member is pure self-service — the
  // events-analytics Dashboard (no cap gate) would be an empty surface, so it
  // hides; landing is My Time. Other workforce roles keep Dashboard.
  "Team Member": ["dashboard"],
  // Queues process (2026-07-17): queue-only personas have no events caps, so
  // the events-analytics Dashboard (no cap gate) would be an empty surface.
  // Keyed on the persona display-role strings whoami returns.
  "Queue Staff · Lobby": ["dashboard"],
  "Queue Staff · Financial aid": ["dashboard"],
  "Queue Staff · Walk-ins": ["dashboard"],
  "Queue Manager · Back Bay": ["dashboard"],
  "Queue Manager · Student Center": ["dashboard"],
  // 3-in-1 rail parity: the retail personas have no Dashboard entry.
  "Store Manager": ["dashboard"],
  "Regional Store Manager": ["dashboard"],
  "Sales Associate": ["dashboard"],
};
export function navHiddenForRole(role?: string): Set<string> {
  return new Set(role ? (NAV_HIDDEN_BY_ROLE[role] || []) : []);
}

/* Default surface a role lands on after sign-in. Event Coordinator lands on
 * Event Day (their primary on-site surface); Event Approver on the Approvals
 * queue (their primary review surface); everyone else on the dashboard.
 *
 * MATCHING IS CASE-INSENSITIVE (2026-07-29). It used to compare the served
 * role label with `===` against a hand-written mix of casings, so a label
 * differing from the table by a single letter's case fell through to
 * `"dashboard"` — silently, with no signal that a mapping had been missed.
 * That is not cosmetic: `dashboard` is itself capability-gated, so a persona
 * without the dashboard capabilities lands on a screen where every tile
 * request 403s (dashboard/summary, dashboard/trends, dashboard/alerts,
 * dashboard/registration-channels, events, storeops/stores) and the first
 * thing the user sees is a page of "Couldn't load …" banners. The
 * authorization behaviour there is CORRECT — the ROUTING is what is wrong,
 * so the fix belongs here and never in a capability check.
 *
 * Two mappings were also genuinely absent, and both fell through to that
 * dashboard 403 storm: "Event Manager" (holds events / guests / campaigns
 * capabilities, no dashboard capability) and "Operations Admin" (holds only
 * journeys.*, services.* and locations.read — the older "Operations admin" →
 * "ops.today" mapping pointed at a workforce surface the role does not hold
 * any capability for). Each now lands on the first surface its own role
 * demonstrably holds a capability for; "Operations Admin" resolves to
 * `journeys`, because `journeys.read` is a capability the role actually
 * holds. */
const LANDING_ROUTE_BY_ROLE: Record<string, string> = {
  // Queuing process (2026-07-17): catalog ids AND the persona display strings.
  "queue staff": "queues.console",
  "queue manager": "queues",
  "event coordinator": "eventday",
  "event approver": "approvals",
  "event manager": "events",
  // Aperture Bank six-persona port (P1): catalog role ids AND the persona
  // display-role strings the sign-in screen passes (e.g. "Workforce admin").
  "workforce admin": "workforce.schedule",
  "branch manager": "workforce.schedule",
  "hr business partner": "workforce.planning",
  "regional lead (banking)": "workforce.region",
  "regional lead · northeast": "workforce.region",
  "personal banker": "workforce.myday",
  "operations admin": "journeys",
  /* Unified administrator, Phase 1 (2026-08-05): the one admin lands on Today in
   * every vertical. Previously absent, so it fell through to `dashboard` — a
   * fallback, never a decision. A tenant's served nav profile may still override
   * this; the profile landing wins when one resolves. */
  "system administrator": "ops.today",
  "workforce scheduler": "workforce.schedule",
  "venue manager": "workforce.schedule",
  "ops lead": "workforce.ops",
  "team member": "workforce.scheduletime.sandbox",
  "regional lead": "workforce.forecast",
  "store manager": "storeops.floor",
  "regional store manager": "storeops.region",
  "sales associate": "storeops.my.day",
};
export function landingRouteForRole(role?: string): string {
  const key = (role || "").trim().toLowerCase();
  if (!key) return "dashboard";
  const exact = LANDING_ROUTE_BY_ROLE[key];
  if (exact) return exact;
  /* Location-qualified labels ("Queue Manager · Back Bay", "Personal Banker
   * — Northgate") land where their base role lands. Longest candidate first
   * so "regional lead (banking)" is never shadowed by "regional lead". */
  const candidates = Object.keys(LANDING_ROUTE_BY_ROLE).sort((a, b) => b.length - a.length);
  for (const candidate of candidates) {
    if (key.startsWith(candidate)) return LANDING_ROUTE_BY_ROLE[candidate]!;
  }
  return "dashboard";
}

/* ─── Approval authority, per content type (roles review, 2026-07-08) ──
 * The Approvals review screen gates its decide actions (approve / reject /
 * request-changes) by the right capability for the submission's type. Events
 * and campaigns have first-class approve caps; the other content types have no
 * dedicated approver cap yet, so their approval is admin-only (settings.write).
 * ApproverReviewScreen uses this to render the decision panel read-only for
 * users who may view but not decide (fixes the previously-ungated buttons). */
const APPROVE_CAP: Record<string, string> = {
  events: "events.approve",
  campaigns: "campaigns.approve",
};
export function canApproveType(type: string): boolean {
  const cap = APPROVE_CAP[type];
  return cap ? can(cap) : can("settings.write");
}

/* ─── Live, editable, persisted role store ─────────────────────────────
 * Seeded from the defaults above; an admin can grant/remove caps per role and
 * the change persists + fans out to can()/useUser() everywhere.
 * v9 (2026-07-13, #803): re-seed so Event Manager picks up the new
 * `audit.read_object` default (the merge lets a persisted v8 set win otherwise).
 * v10 (2026-07-13, PM role-access ingest): Manager +inbox.read/write
 * +delivery.read; Approver +reports.export; Creator −services.read/write.
 *
 * ⚠ INTERIM CLIENT-SIDE PERSISTENCE — NOT A BACKEND RECORD (CC GM14 §3 item 3).
 * An admin's runtime capability grants/revokes round-trip through localStorage
 * only: browser-local, non-durable, non-shared, and NOT audit-logged. This is
 * design-preview scaffolding, never a substitute for the identity worker's
 * role/capability store. REQ-088 asks for the real persistence ops
 * (role-permission read/write in the identity worker, capability-gated,
 * audit-chained); when they land, seedRoleStore/persistRoleStore collapse into
 * typed op calls with zero UI change. Until then this store is carved OUT of
 * any "seed-data 0" claim in drop covers — it is disclosed interim state. */
const ROLE_STORE_KEY_BASE = "jrni_role_perms_v10";
const roleStoreKey = (slug: string | null) => `${ROLE_STORE_KEY_BASE}:${slug ?? "none"}`;
type RoleStore = Record<string, string[]>; // `${process}:${role}` -> perms[]
const roleKey = (process: ProcessId, role: string) => `${process}:${role}`;

function seedRoleStore(): RoleStore {
  const s: RoleStore = {};
  ALL_ROLE_DEFS.forEach((d) => { s[roleKey(d.process, d.id)] = [...d.perms]; });
  return s;
}
// Tenant-scoping fix (docket 2026-07-27, item 1 — "role permissions were not
// given tenant-switch handling"): this was ONE flat unscoped key, so a role
// customised under tenant A silently applied to every other tenant in the
// same browser too. ROLE_STORE now starts as pure defaults; the real
// per-tenant load happens once a tenant is CONFIRMED (see the
// onTenantSwitching/onTenantChange registrations after the tenant-identity
// section below) — never eagerly here, when no tenant is known yet.
let ROLE_STORE: RoleStore = seedRoleStore();
let _roleStoreTenantSlug: string | null = null;
function loadRoleStoreForTenant(slug: string | null): void {
  ROLE_STORE = seedRoleStore();
  try {
    const raw = localStorage.getItem(roleStoreKey(slug));
    if (raw) { const saved = JSON.parse(raw) as RoleStore; ROLE_STORE = { ...ROLE_STORE, ...saved }; }
  } catch { /* noop */ }
  _roleStoreTenantSlug = slug;
}
function persistRoleStore() { try { localStorage.setItem(roleStoreKey(_roleStoreTenantSlug), JSON.stringify(ROLE_STORE)); } catch { /* noop */ } }

export function getRolePermsLive(process: ProcessId, role: string): string[] {
  // System roles (System Administrator) always resolve to the live catalog
  // ceiling, never a persisted subset — a store seeded before newer caps
  // existed would otherwise silently strip them from the admin (admin
  // full-access validation, 2026-07-10, ingested 2026-07-13).
  const def = getRoleDef(role, process);
  if (def?.system) return def.perms;
  return ROLE_STORE[roleKey(process, role)] ?? roleDefaultPerms(process, role);
}
export function setRolePerms(process: ProcessId, role: string, perms: string[]) {
  // A system role's set can never be narrowed or persisted (belt-and-braces
  // with the SettingsTeam UI lock on `system` roles).
  if (getRoleDef(role, process)?.system) return;
  ROLE_STORE[roleKey(process, role)] = [...new Set(perms)].filter((p) => CAP_SET.has(p));
  persistRoleStore(); notify();
}
export function toggleRolePerm(process: ProcessId, role: string, capId: string, on: boolean) {
  const cur = new Set(getRolePermsLive(process, role));
  if (on) cur.add(capId); else cur.delete(capId);
  setRolePerms(process, role, [...cur]);
}
export function resetRole(process: ProcessId, role: string) {
  ROLE_STORE[roleKey(process, role)] = [...roleDefaultPerms(process, role)];
  persistRoleStore(); notify();
}
export function isRoleCustomised(process: ProcessId, role: string): boolean {
  const a = [...getRolePermsLive(process, role)].sort();
  const b = [...roleDefaultPerms(process, role)].sort();
  return JSON.stringify(a) !== JSON.stringify(b);
}

/* Back-compat: ROLE_PERMS[name] reads the LIVE events set; enumerable so
 * `Object.keys(ROLE_PERMS)` and `flatMap` still work. */
export const ROLE_PERMS: Record<string, string[]> = new Proxy({}, {
  get: (_t, k) => getRolePermsLive("events", String(k)),
  has: (_t, k) => listRoleNames("events").includes(String(k)),
  ownKeys: () => listRoleNames("events"),
  getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true }),
}) as Record<string, string[]>;

/* ─── Custom roles ─ admin-saved subsets of a built-in's ceiling ──────── */
export interface CustomRole { perms: string[]; basedOn: string; isCustom: true; }
let CUSTOM_ROLES: Record<string, CustomRole> = {};
export function addCustomRole(name: string, perms: string[], basedOn: string): boolean {
  const trimmed = (name || "").trim();
  if (!trimmed || listRoleNames("events").includes(trimmed) || CUSTOM_ROLES[trimmed]) return false;
  const ceiling = new Set(getRolePermsLive("events", basedOn));
  CUSTOM_ROLES = { ...CUSTOM_ROLES, [trimmed]: { perms: perms.filter((p) => ceiling.has(p)), basedOn, isCustom: true } };
  notify(); return true;
}
export function removeCustomRole(name: string): boolean {
  if (!CUSTOM_ROLES[name]) return false;
  const next = { ...CUSTOM_ROLES }; delete next[name]; CUSTOM_ROLES = next; notify(); return true;
}
export function getCustomRoles(): Record<string, CustomRole> { return CUSTOM_ROLES; }
export function subscribeCustomRoles(fn: () => void) { return subscribe(fn); }

export function getRolePerms(roleKeyName?: string): string[] {
  if (!roleKeyName) return [];
  if (listRoleNames("events").includes(roleKeyName)) return getRolePermsLive("events", roleKeyName);
  if (CUSTOM_ROLES[roleKeyName]) return CUSTOM_ROLES[roleKeyName].perms;
  return [];
}

/* ─── Change bus ─ one subscription for role edits, member edits, user swaps ── */
const subs = new Set<() => void>();
function subscribe(fn: () => void) { subs.add(fn); return () => { subs.delete(fn); }; }
function notify() { subs.forEach((f) => { try { f(); } catch { /* noop */ } }); }
export function subscribeAccess(fn: () => void) { return subscribe(fn); }

/* ─── Tenant identity (#659) ────────────────────────────────────────────────────
 * The session's real tenant, from GET /auth/whoami (`session.tenant.displayName`
 * / `.slug` per the WhoamiResponse contract). NO baked default — while loading
 * (or if whoami is unavailable) this returns null and consumers render nothing
 * rather than a placeholder tenant name. */
export interface TenantIdentity { id: string; slug: string; name: string; plan?: string; }
let TENANT: TenantIdentity | null = null;
let _tenantFetched = false;
// REQ-045: the session's active-organization projection, surfaced at the top
// level of whoami() (organizationId/organizationName, additive-only — both
// absent for a non-demo/unscoped session). Distinct from tenant/workspace:
// an org can be set/cleared within a tenant. Threaded into getTranslationCatalog
// so org-scoped catalog overrides (e.g. Rivière jargon) apply automatically.
let ACTIVE_ORG_ID: string | null = null;
let ACTIVE_ORG_NAME: string | null = null;
export function getActiveOrganizationId(): string | null { return ACTIVE_ORG_ID; }
export function useActiveOrganization(): { id: string; name: string | null } | null {
  const [, force] = useState(0);
  useEffect(() => { fetchTenant(); return subscribe(() => force((n) => n + 1)); }, []);
  return ACTIVE_ORG_ID ? { id: ACTIVE_ORG_ID, name: ACTIVE_ORG_NAME } : null;
}
function applyActiveOrganization(raw: Record<string, any> | null | undefined): void {
  const src = (raw?.session ?? raw ?? {}) as Record<string, any>;
  const orgId = typeof src.organizationId === "string" ? src.organizationId : null;
  ACTIVE_ORG_ID = orgId;
  ACTIVE_ORG_NAME = typeof src.organizationName === "string" ? src.organizationName : null;
  void refreshTranslationCatalog(orgId);
}
function applyTenantIdentity(raw: Record<string, any> | null | undefined): boolean {
  const t = (raw as any)?.session?.tenant ?? (raw as any)?.tenant;
  if (!t) return false;
  TENANT = {
    id: t.id ?? t.slug ?? "",
    slug: t.slug ?? t.id ?? "",
    name: t.displayName ?? t.display_name ?? t.name ?? t.slug ?? "",
    plan: t.plan ?? undefined,
  };
  return true;
}
function fetchTenant() {
  if (_tenantFetched) return;
  _tenantFetched = true;
  if (typeof whoami !== "function") return;
  whoami({ baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" } as never)
    .then((r: unknown) => {
      const raw = r as Record<string, any>;
      applyActiveOrganization(raw);
      if (!applyTenantIdentity(raw)) return;
      notify();
      // Initial identity resolution is not a tenant CHANGE. Broadcasting it
      // here caused every screen module imported by SCREENS to run its
      // onTenantChange hydrator, eagerly requesting unrelated admin surfaces
      // that the signed-in persona could not access. Active screens already
      // hydrate through their own hooks. Real workspace switches still call
      // _fireTenantChange() after the replacement session is confirmed.
    })
    .catch((e: unknown) => console.warn("whoami failed — tenant identity unavailable:", e));
}
/** The signed-in tenant, or null while loading. Re-renders when it resolves. */
export function getActiveTenantSlug(): string | null { return TENANT?.slug ?? null; }
export function useTenant(): TenantIdentity | null {
  const [, force] = useState(0);
  useEffect(() => { fetchTenant(); return subscribe(() => force((n) => n + 1)); }, []);
  return TENANT;
}

/* ─── Workspaces the user can access (account switcher) ──────────────────
 * The account-list dropdown + Account › Workspaces read the same canonical list:
 * getAccountDetails().workspaces (AccountWorkspace[]). Fetched once and cached.
 * `switchWorkspace` re-scopes the active workspace — see the note on the real op. */
export interface Workspace {
  id: string; slug: string; name: string; brandColor: string; initials: string;
  plan?: string; role?: string; eventCount?: number; current: boolean;
}
const ACCOUNT_OPTS = { baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" } as never;
function toWorkspace(w: Record<string, any>): Workspace {
  const name = w.displayName || w.name || w.slug || "";
  return {
    id: w.id ?? w.slug ?? "", slug: w.slug ?? w.id ?? "", name,
    brandColor: w.brandColor || T.p1, initials: w.initials || initialsOf(name),
    plan: w.plan ?? undefined, role: w.role ?? undefined,
    eventCount: typeof w.eventCount === "number" ? w.eventCount : undefined,
    current: !!w.current,
  };
}
let WORKSPACES: Workspace[] = [];
let _workspacesFetched = false;
function fetchWorkspaces() {
  if (_workspacesFetched) return;
  _workspacesFetched = true;
  if (typeof getAccountDetails !== "function") return;
  (getAccountDetails as (o: never) => Promise<unknown>)(ACCOUNT_OPTS)
    .then((r: unknown) => {
      const raw = r as Record<string, any>;
      const acct = raw.account ?? raw.data ?? raw;
      const rows: any[] = Array.isArray(acct?.workspaces) ? acct.workspaces : [];
      WORKSPACES = rows.map(toWorkspace);
      notify();
    })
    .catch((e: unknown) => console.warn("getAccountDetails (workspaces) failed:", e));
}
/** The workspaces the current user can switch between. Re-renders when loaded. */
export function useWorkspaces(): Workspace[] {
  const [, force] = useState(0);
  useEffect(() => { fetchWorkspaces(); return subscribe(() => force((n) => n + 1)); }, []);
  return WORKSPACES;
}

/** Subscribe to be notified exactly when the ACTIVE TENANT changes (a
 * workspace switch) — narrower than the general `subscribe` pub-sub above,
 * which fires on every access-state change. Registrants use this to purge
 * tenant-scoped module caches SYNCHRONOUSLY on an in-place switch (the
 * store-switcher cross-tenant leak, docket 2026-07-26 CD-PUSHBACK-STORE-
 * SWITCHER: `switchWorkspace` doesn't reload the page in this preview, so a
 * module-scope cache that isn't keyed by tenant identity survives into the
 * new tenant's session). Returns an unsubscribe fn. */
const _tenantChangeSubs = new Set<() => void>();
/** Subscribe to be notified the INSTANT a tenant switch is optimistically
 * initiated — BEFORE the session-switch op resolves. Registrants purge
 * tenant-scoped module caches SYNCHRONOUSLY here (never show tenant A's data
 * under tenant B's chrome) WITHOUT issuing any new network request yet —
 * fetching happens only once onTenantChange (below) confirms the new session
 * is actually live. This is the fix for the request-order race in docket
 * 2026-07-27 (CD-DOCKET-OVERNIGHT item 1): a request fired at THIS instant
 * would still run under the OUTGOING tenant's session against a real
 * backend, yet would be accepted as the incoming tenant's data because the
 * client-side slug already matches. Returns an unsubscribe fn. */
const _tenantSwitchingSubs = new Set<() => void>();
export function onTenantSwitching(fn: () => void): () => void {
  _tenantSwitchingSubs.add(fn);
  return () => { _tenantSwitchingSubs.delete(fn); };
}
function _fireTenantSwitching() {
  _tenantSwitchingSubs.forEach((f) => { try { f(); } catch (e) { console.warn("onTenantSwitching handler failed:", e); } });
}

let _tenantGeneration = 0;
let _switchPending = false;
/** Increments every time the CONFIRMED active tenant changes (initial
 * resolution, a completed switch, or a rollback). */
export function getTenantGeneration(): number { return _tenantGeneration; }
/** The CONFIRMED active tenant slug — null before anything has resolved, AND
 * null for the duration of a pending workspace switch (between the
 * optimistic update and its confirmation/rollback). Tenant-scoped stores
 * should key and gate their fetches off THIS, never off the optimistic
 * identity `useTenant()`/the chrome reads for instant display. */
export function getConfirmedTenantSlug(): string | null {
  return _switchPending ? null : getActiveTenantSlug();
}

/** Subscribe to be notified once the ACTIVE TENANT is CONFIRMED — initial
 * whoami resolution, a successful workspace-switch confirmation, or a
 * rollback to the previous tenant after a failed switch. This is the
 * "safe to fetch" signal: by the time this fires, `getConfirmedTenantSlug()`
 * reflects a session that is actually live server-side, never optimistic.
 * Registrants purge on `onTenantSwitching` and (re)fetch here. Returns an
 * unsubscribe fn. */
export function onTenantChange(fn: () => void): () => void {
  _tenantChangeSubs.add(fn);
  return () => { _tenantChangeSubs.delete(fn); };
}
function _fireTenantChange() {
  _switchPending = false;
  _tenantGeneration++;
  _tenantChangeSubs.forEach((f) => { try { f(); } catch (e) { console.warn("onTenantChange handler failed:", e); } });
}

/** Switch the active workspace (REQ-006 / #account-switch, resolved 2026-07-05).
 *
 * The real `switchWorkspace` op is a NAVIGATION resolver, not an in-place
 * re-scope: production sessions are per-tenant-slug cookies, so it validates the
 * target and hands back whoami-shaped hydration + the target `studioHost` to
 * navigate to. We optimistically update the chrome immediately, then resolve
 * against the identity worker; if it returns a real `studioHost` URL we navigate
 * there (that's what actually re-scopes the data plane). In the in-browser
 * preview the mock returns a scheme-less host, so we stay put with the optimistic
 * update — the switcher + Account › Workspaces call sites never change. */
let _switchGen = 0;
export function switchWorkspace(workspaceId: string): void {
  const ws = WORKSPACES.find((w) => w.id === workspaceId);
  if (!ws || ws.current) return;
  const prevTenant = TENANT;
  const prevWorkspaces = WORKSPACES;
  const gen = ++_switchGen; // generation guard (docket 2026-07-27 item 1): a stale switch's late response must never win over a newer one
  WORKSPACES = WORKSPACES.map((w) => ({ ...w, current: w.id === workspaceId }));
  TENANT = { id: ws.id, slug: ws.slug, name: ws.name, plan: ws.plan }; // optimistic — chrome (name/avatar) updates instantly
  _switchPending = true; // no tenant-scoped store may fetch until this clears
  notify();
  _fireTenantSwitching(); // purge every registered cache SYNCHRONOUSLY — never show tenant A's data under tenant B's chrome — nobody fetches yet
  if (typeof switchWorkspaceOp !== "function") { _fireTenantChange(); return; } // no real op wired — treat the optimistic state as confirmed so stores aren't stuck empty forever
  (switchWorkspaceOp as (id: string, o: never) => Promise<unknown>)(workspaceId, ACCOUNT_OPTS)
    .then((r: unknown) => {
      if (gen !== _switchGen) return; // superseded by a newer switch — a stale confirmation must never fire
      const res = r as Record<string, any>;
      const wsr = (res?.workspace ?? {}) as Record<string, any>;
      TENANT = {
        id: wsr.id ?? ws.id, slug: wsr.slug ?? ws.slug,
        name: wsr.displayName ?? wsr.name ?? ws.name, plan: wsr.plan ?? ws.plan,
      };
      if (res?.whoami) applyWhoamiUser(res.whoami);
      applyActiveOrganization(res?.whoami);
      notify();
      _fireTenantChange(); // session confirmed live — safe for every tenant-scoped store to fetch tenant B's data now
      const host = res?.studioHost;
      if (typeof host === "string" && /^https?:\/\//.test(host) && typeof window !== "undefined") {
        window.location.assign(host);
      }
    })
    .catch((e: unknown) => {
      if (gen !== _switchGen) return; // a newer switch already superseded this attempt
      console.warn("switchWorkspace failed:", e);
      // Roll back the optimistic identity (docket 2026-07-27 item 1) — never
      // leave the chrome claiming a tenant whose session never actually switched.
      WORKSPACES = prevWorkspaces;
      TENANT = prevTenant;
      notify();
      toast({ tone: "danger", title: i18nT("shell.access.couldnTSwitchWorkspaces", "Couldn't switch workspaces"), subtitle: i18nT("shell.access.youReStillOnThePreviousWorkspaceRetryInAMoment", "You're still on the previous workspace. Retry in a moment.") });
      _fireTenantSwitching(); // purge again — clear anything a store began showing for the failed target tenant
      _fireTenantChange(); // back on the previous (already-live) tenant — safe to reload its data immediately
    });
}

/* ─── Current user + effective permissions ─────────────────────────────
 * A user has a role per process (assignments) plus additive grants/revokes.
 * Effective = ∪(role defaults over assigned processes) ∪ grants − revokes. */
export interface CurrentUser {
  id?: string; name?: string; email?: string; role?: string;
  init?: string; color?: string;
  /** Role per process. Falls back to { events: role } when absent. */
  assignments?: Partial<Record<ProcessId, string>> | null;
  /** Additive per-user capabilities above the role. */
  grants?: string[] | null;
  /** Capabilities removed from the role for this user. */
  revokes?: string[] | null;
  /** Direct snapshot (impersonation / tenant override) — wins over role resolution. */
  permissions?: string[] | null;
  locations?: string[] | null;
}

export function effectivePerms(u: { role?: string; assignments?: Partial<Record<ProcessId, string>> | null; grants?: string[] | null; revokes?: string[] | null }): Set<string> {
  const assignments = u.assignments && Object.keys(u.assignments).length ? u.assignments : (u.role ? { events: u.role } : {});
  const set = new Set<string>();
  (Object.entries(assignments) as [ProcessId, string][]).forEach(([proc, role]) => {
    getRolePermsLive(proc, role).forEach((p) => set.add(p));
  });
  (u.grants || []).forEach((p) => { if (CAP_SET.has(p)) set.add(p); });
  (u.revokes || []).forEach((p) => set.delete(p));
  return set;
}

// #655 (2026-07-04): no baked identity in app source. CURRENT_USER is empty
// until a demo sign-in mints a session; signIn() hydrates it from whoami().
let CURRENT_USER: CurrentUser = {};

export function setUser(u: CurrentUser) { CURRENT_USER = u; notify(); }
export function getUser(): CurrentUser { return CURRENT_USER; }

/** Re-renders on any access change (user / role edit / member edit / impersonation). */
export function useUser(): CurrentUser {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), []);
  return CURRENT_USER;
}

/** Permission check — reads the current user. Direct snapshot wins; else effective. */
export function can(perm: string): boolean {
  const u = CURRENT_USER;
  if (Array.isArray(u.permissions) && u.permissions.length) return u.permissions.includes(perm);
  return effectivePerms(u).has(perm);
}

/** True when the current (or impersonated) user holds full administrative
 * authority. Phase 1 (2026-08-05): this was a comparison against the literal
 * role name "System Administrator", which meant renaming the role silently
 * disabled every governance affordance hanging off it (audit trail, AI
 * settings, ticket price-lock, branding audit). It is now a capability check,
 * so the role's NAME and its AUTHORITY are no longer the same fact. */
export function isAdmin(): boolean {
  return can("admin.super");
}

/* ─── Team members ─ API-backed store the admin screen edits ───────────
 * #651/#659 (2026-07-04): the fictional seeded roster was removed — members
 * hydrate from the real `listTeamMembers` client op.
 *
 * UIR-01 round 2 (2026-07-25): the member WRITES are now the real ops too —
 * `inviteTeamMember` / `updateTeamMemberRole` / `resendTeamInvite` /
 * `removeTeamMember` are declared in the contract, exported from the typed
 * client and fully mock-served. (The earlier remediation pass disabled these
 * CTAs as "no declared op yet" on a stale premise — see the scrub audit.)
 *
 * Role vocabulary: team administration is TENANT-role scoped (`roleDefinitions`
 * on the list response — owner/admin/member/…), which is a DIFFERENT namespace
 * from this file's Events-PROCESS role catalog that `can()` resolves against.
 * Writes therefore carry the API role KEY (`Member.roleKey`), never the local
 * process-role label — no invented mapping between the two. */
export interface Member {
  id: string; name: string; email: string; init: string; color: string;
  /** Display role label — the tenant role's label when API-sourced, else the process role. */
  role: string;
  /** Tenant role key as the API knows it (`roleDefinitions[].key`). Empty for non-API rows. */
  roleKey: string;
  /** Events-PROCESS role this member maps onto (what `can()`/impersonation resolve
   *  against). Empty when the tenant role has no process-role counterpart. */
  processRole: string;
  /** Membership status from the API (invited rows can be resent). */
  status: string;
  /** Capability ids the API reports for this member (authoritative when API-sourced). */
  apiCaps: string[] | null;
  /** Role per process, verbatim from the seed persona (the SAME vocabulary
   *  `can()` resolves — what capability counts and impersonation read). Null for
   *  rows from sources that predate assignments. */
  assignments: Partial<Record<ProcessId, string>> | null;
  grants: string[]; revokes: string[];
  last: string;
}

/** A tenant role as the team API defines it — the vocabulary team writes use. */
export interface ApiRole {
  key: string; label: string; description: string; isCustom: boolean;
  capabilities: string[]; permissions: string[]; memberCount: number;
}
let API_ROLES: ApiRole[] = [];
/** Tenant roles from the last `listTeamMembers` response (empty until it lands). */
export function listApiRoles(): ApiRole[] { return API_ROLES; }
export function getApiRole(key: string): ApiRole | undefined { return API_ROLES.find((r) => r.key === key); }
const TEAM_OPTS = { baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" } as never;
const MEMBER_COLORS = ["#5142C7", "#1F8A5B", "#C77F14", "#C13584", "#2D60EB", "#8955F2", "#0E7490", "#D01647"]; // ui-config
export function initialsOf(s: string) { return (s || "?").split(/[@.\s-]+/).filter(Boolean).map((p) => p[0]).join("").toUpperCase().slice(0, 2) || "?"; }
export function colorFor(s: string) { return MEMBER_COLORS[(s || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % MEMBER_COLORS.length]; }

/* API role slug → events-process role label. */ // ui-config
const API_ROLE_MAP: Record<string, string> = {
  admin: "System Administrator", administrator: "System Administrator",
  manager: "Event Manager", coordinator: "Event Coordinator",
  creator: "Event Creator", editor: "Event Creator", approver: "Event Approver",
};
function toMemberFromApi(m: Record<string, any>): Member {
  const name = (m.displayName ?? m.name ?? (`${m.firstName ?? m.first_name ?? ""} ${m.lastName ?? m.last_name ?? ""}`).trim()) || m.email || "";
  const roleRaw = (m.role ?? m.roleId ?? "").toString();
  // Prefer the tenant role's own label from roleDefinitions; fall back to the
  // legacy slug→process-role map, then the raw key.
  const apiRole = getApiRole(roleRaw);
  // Persona-consolidation alignment (2026-08-06): rows served from personas.json
  // carry `assignments` (per-process roles) and `displayRole` (the persona's own
  // label). When assignments are present THEY decide the process role — the
  // legacy slug→events map must not run, or every rbacLevel:"admin" persona
  // would silently resolve to System Administrator.
  const rawAssign = (m.assignments && typeof m.assignments === "object") ? m.assignments as Record<string, string> : null;
  const assignments: Partial<Record<ProcessId, string>> = {};
  if (rawAssign) {
    (Object.entries(rawAssign) as [ProcessId, string][]).forEach(([proc, r0]) => {
      if (PROCESSES.some((p) => p.id === proc) && typeof r0 === "string" && r0) assignments[proc] = r0;
    });
  }
  const displayRole = typeof m.displayRole === "string" && m.displayRole ? m.displayRole : "";
  return {
    id: m.id ?? "m-" + (m.email ?? name).split("@")[0],
    name, email: m.email ?? "",
    init: initialsOf(name), color: colorFor(m.email ?? name),
    role: displayRole || (apiRole?.label ?? API_ROLE_MAP[roleRaw.toLowerCase()] ?? roleRaw),
    roleKey: roleRaw,
    processRole: assignments.events ?? (rawAssign ? "" : (API_ROLE_MAP[roleRaw.toLowerCase()] ?? "")),
    assignments: rawAssign ? assignments : null,
    status: (m.status ?? "active").toString(),
    /* An EMPTY capability array is not an authoritative "zero capabilities" —
       the served roster carries [] because process roles resolve client-side.
       Treating it as authoritative printed "0 caps" against real people. */
    apiCaps: Array.isArray(m.capabilities) && m.capabilities.length ? m.capabilities as string[] : null,
    grants: m.grants ?? [], revokes: m.revokes ?? [],
    last: m.lastActiveLabel ?? m.lastActiveAt ?? m.last_active_at ?? m.last ?? "—",
  };
}

let MEMBERS: Member[] = [];
let _membersFetched = false;
/* A FAILED member read is not "no members" (2026-08-06). It used to vanish into
 * a console.warn, so every consumer saw an empty roster and had no way to tell
 * the two apart — People then rendered eleven staff records and asserted that
 * real, logged-in people had no sign-in. Exposed so a screen can disclose it. */
let MEMBERS_ERROR: string | null = null;
/** Set only when `listTeamMembers` actually rejected; null while loading or on success. */
export function getMembersError(): string | null { return MEMBERS_ERROR; }
export function hydrateMembers() {
  if (_membersFetched) return;
  _membersFetched = true;
  if (typeof listTeamMembers !== "function") return;
  listTeamMembers({}, TEAM_OPTS)
    .then((r: unknown) => {
      const raw = r as Record<string, any>;
      MEMBERS_ERROR = null;
      const roleRows: any[] = Array.isArray(raw.roleDefinitions) ? raw.roleDefinitions : [];
      if (roleRows.length) {
        API_ROLES = roleRows.map((r0) => ({
          key: String(r0.key ?? r0.id ?? ""), label: String(r0.label ?? r0.key ?? ""),
          description: String(r0.description ?? ""), isCustom: !!r0.isCustom,
          capabilities: Array.isArray(r0.capabilities) ? r0.capabilities : [],
          permissions: Array.isArray(r0.permissions) ? r0.permissions : [],
          memberCount: typeof r0.memberCount === "number" ? r0.memberCount : 0,
        }));
      }
      const rows: any[] = raw.members ?? raw.team ?? raw.data ?? [];
      if (Array.isArray(rows) && rows.length) { MEMBERS = rows.map(toMemberFromApi); }
      if (roleRows.length || rows.length) notify();
    })
    .catch((e: unknown) => { MEMBERS_ERROR = "Couldn't load the team roster."; console.warn("listTeamMembers failed — roster unavailable:", e); notify(); });
}
/** Re-read the roster from the API (after a write). */
export function refetchMembers(): Promise<void> {
  _membersFetched = false;
  hydrateMembers();
  return Promise.resolve();
}
// Team roster + role-permission leak fix (docket 2026-07-26 & 2026-07-27):
// MEMBERS/API_ROLES/ROLE_STORE/CUSTOM_ROLES were fetched/persisted once with
// no tenant keying and no confirmation gate — purge SYNCHRONOUSLY the instant
// a switch starts (never show tenant A's roster/role-customisations under
// tenant B's chrome); reload only once the tenant is CONFIRMED (a request
// fired mid-switch would still run under the outgoing tenant's session
// against a real backend). `_membersFetched = true` during the pending
// window actively BLOCKS any incidental hydrateMembers() call in that gap
// (mirrors the guard hydrateMembers() already checks).
onTenantSwitching(() => { MEMBERS = []; API_ROLES = []; _membersFetched = true; ROLE_STORE = seedRoleStore(); CUSTOM_ROLES = {}; notify(); });
// Capability gate (docket 2026-07-31, persona-inappropriate prefetch): this is
// a module-level registration in the shell's own auth module, so it ran for
// every signed-in session regardless of role — the admin/team/members 403. The
// flag still resets unconditionally, and hydrateMembers() stays exported and
// ungated, so SettingsTeam (route-guarded on team.read) and refetchMembers()
// after a write both still work on demand.
onTenantChange(() => { _membersFetched = false; if (can("team.read")) hydrateMembers(); loadRoleStoreForTenant(getActiveTenantSlug()); notify(); });

export function listMembers(): Member[] { return MEMBERS; }
export function getMember(id: string): Member | undefined { return MEMBERS.find((m) => m.id === id); }

/* ── Member writes ─ real ops, awaited, reconciled from the response ────
 * Each throws on failure so the caller can render an honest error state;
 * nothing commits locally unless the server accepted the write. */

/** Change a member's tenant role (PATCH /admin/team/members/{id}). */
export async function setMemberRoleOp(id: string, roleKey: string): Promise<Member> {
  const res = await updateTeamMemberRole(id, { role: roleKey }, TEAM_OPTS) as unknown as Record<string, any>;
  const saved = toMemberFromApi(res.member ?? {});
  MEMBERS = MEMBERS.map((m) => (m.id === id ? saved : m));
  notify();
  return saved;
}

/** Invite a teammate (POST /admin/team/members). `capabilities`, when given,
 *  may only NARROW the role's set — the API rejects grants above the role. */
export async function inviteMemberOp(input: { email: string; roleKey: string; displayName?: string; inviteNote?: string; capabilities?: string[] }): Promise<Member> {
  const body: Record<string, unknown> = { email: input.email.trim(), role: input.roleKey };
  if (input.displayName?.trim()) body.displayName = input.displayName.trim();
  if (input.inviteNote?.trim()) body.inviteNote = input.inviteNote.trim();
  if (input.capabilities) body.capabilities = input.capabilities;
  const res = await inviteTeamMember(body as never, TEAM_OPTS) as unknown as Record<string, any>;
  const saved = toMemberFromApi(res.member ?? {});
  MEMBERS = [saved, ...MEMBERS];
  notify();
  return saved;
}

/** Resend a pending invite (POST /admin/team/members/{id}/resend). */
export async function resendInviteOp(id: string): Promise<void> {
  const res = await resendTeamInvite(id, TEAM_OPTS) as unknown as Record<string, any>;
  if (res?.member) {
    const saved = toMemberFromApi(res.member);
    MEMBERS = MEMBERS.map((m) => (m.id === id ? saved : m));
    notify();
  }
}

/** Revoke a membership (DELETE /admin/team/members/{id}). */
export async function removeMemberOp(id: string): Promise<void> {
  await removeTeamMemberOp(id, TEAM_OPTS);
  MEMBERS = MEMBERS.filter((m) => m.id !== id);
  notify();
}
export function subscribeMembers(fn: () => void) { return subscribe(fn); }

/** Effective capabilities for a member. Resolves against the per-process
 *  `assignments` when the row carries them (persona-sourced rows do); else the
 *  events-process fallback — the tenant role label is a display string, not a
 *  `can()` key. */
export function memberEffective(m: Member): Set<string> {
  return effectivePerms({ role: m.processRole || m.role, assignments: m.assignments ?? undefined, grants: m.grants, revokes: m.revokes });
}
/** The ceiling a member can be granted within — the union of their assigned
 *  roles' default sets across processes. */
export function memberCeiling(m: Member): string[] {
  if (m.assignments && Object.keys(m.assignments).length) {
    const set = new Set<string>();
    (Object.entries(m.assignments) as [ProcessId, string][]).forEach(([proc, role]) => {
      getRolePermsLive(proc, role).forEach((p) => set.add(p));
    });
    return [...set];
  }
  return getRolePermsLive("events", m.processRole || m.role);
}

/* ─── Sign-in personas + auth state ────────────────────────────────────
 * The mobile/tablet login is a persona chooser: pick a role to sign in as.
 * (The preview bundles against a stubbed backend; the real `login` client op
 * takes over server-side with no UI change.) Each persona maps to one of the
 * built-in events roles so the launcher + Event Day reflect real capabilities. */
export interface Persona {
  id: string; name: string; email: string; role: string;
  init: string; color: string; blurb: string;
}

// Stable display hue per persona id (UI-only presentation, not identity data). // ui-config
const PERSONA_HUES: Record<string, string> = {
  "p-coordinator": "#C77F14", "p-manager": "#0E7490", "p-creator": "#2D60EB",
  "p-approver": "#027D47", "p-admin": "#5142C7",
};
function toPersona(d: Record<string, any>): Persona {
  const name = d.displayName ?? d.name ?? d.email ?? "";
  return {
    id: d.id, name, email: d.email ?? "", role: d.role ?? "",
    init: initialsOf(name), color: PERSONA_HUES[d.id] ?? colorFor(d.id ?? name),
    blurb: d.blurb ?? "",
  };
}
/** Fetch the demo persona chooser options (getDemoPersonas → GET /demo/personas). */
export async function fetchPersonas(): Promise<Persona[]> {
  if (typeof getDemoPersonas !== "function") return [];
  try {
    const list = await getDemoPersonas();
    return (Array.isArray(list) ? list : []).map(toPersona);
  } catch (e) { console.warn("getDemoPersonas failed:", e); return []; }
}

let SIGNED_IN = false;
export function isSignedIn(): boolean { return SIGNED_IN; }

function applyWhoamiUser(w: Record<string, any>) {
  const u = w?.user ?? w?.session?.user ?? {};
  const role = u.role ?? "";
  // Per-process assignments (workforce spec 2026-07-16): whoami may return
  // user.assignments = { events?: role, workforce?: role, … }. Falls back to
  // the single-role shape.
  const rawAssign = (u.assignments && typeof u.assignments === "object") ? u.assignments as Record<string, string> : null;
  const assignments: Partial<Record<ProcessId, string>> = {};
  if (rawAssign) {
    (Object.entries(rawAssign) as [ProcessId, string][]).forEach(([proc, r]) => {
      if (PROCESSES.some((p) => p.id === proc) && typeof r === "string" && r) assignments[proc] = r;
    });
  }
  if (!Object.keys(assignments).length && role) assignments.events = role;
  CURRENT_USER = {
    id: u.id ?? "", name: u.name ?? "", email: u.email ?? "", role,
    init: initialsOf(u.name ?? u.email ?? ""), color: colorFor(u.email ?? u.id ?? ""),
    assignments,
    grants: [], revokes: [],
    permissions: Array.isArray(u.permissions) && u.permissions.length ? u.permissions : null,
  };
}

/** Demo sign-in: mint a real role-scoped session for `personaId`, then hydrate
 *  the current user from whoami() — no baked identity. (#655) */
export async function signIn(personaId: string): Promise<void> {
  IMPERSONATION = null;
  if (typeof demoSignIn === "function") {
    try { await demoSignIn(personaId); } catch (e) { console.warn("demoSignIn failed:", e); }
  }
  let hydrated = false;
  if (typeof whoami === "function") {
    try {
      const w = await whoami({ baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" } as never) as Record<string, any>;
      if (w && (w.user ?? w.session?.user)) { applyWhoamiUser(w); hydrated = true; }
      // 2026-07-29: the new session's TENANT/org must be applied here too.
      // `fetchTenant` is one-shot (`_tenantFetched`), so without this the shell
      // kept whatever tenant the PAGE resolved at first load — sign in as a bank
      // persona after the page had resolved another org and you got that org's
      // chrome and its nav composition (no matching profile → generic rail).
      // Tenant-scoped caches are purged, then told to refetch.
      _tenantFetched = true;
      const tenantChanged = applyTenantIdentity(w);
      applyActiveOrganization(w);
      if (tenantChanged) _fireTenantSwitching();
      SIGNED_IN = true; // stores gate their fetch on isSignedIn()
      if (tenantChanged) _fireTenantChange();
    } catch (e) { console.warn("whoami after demoSignIn failed:", e); }
  }
  if (!hydrated) { CURRENT_USER = { id: personaId, assignments: {}, grants: [], revokes: [] }; }
  SIGNED_IN = true;
  notify();
}
export function signOut() { SIGNED_IN = false; IMPERSONATION = null; CURRENT_USER = {}; notify(); }

/** Host-driven real-SSO bypass (packet #3 §3). The outer shell resolves its own
 *  authenticated identity (real whoami) and calls this once, before/at mount, to
 *  mark the session authenticated and hydrate the current user — skipping the demo
 *  persona chooser entirely. Nothing here touches demo-persona state. */
export function setAuthenticated(user: CurrentUser): void {
  IMPERSONATION = null;
  CURRENT_USER = {
    ...user,
    assignments: user.assignments ?? (user.role ? { events: user.role } : {}),
    grants: user.grants ?? [],
    revokes: user.revokes ?? [],
  };
  SIGNED_IN = true;
  notify();
}
/** Re-renders on sign-in/out (and any access change). */
export function useAuth(): { signedIn: boolean; user: CurrentUser } {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), []);
  return { signedIn: SIGNED_IN, user: CURRENT_USER };
}

/* ─── Impersonation ─ admin "view as" another member ───────────────────── */
export interface Impersonation {
  realUser: CurrentUser;
  target: CurrentUser;
  /** When this acting-as session began — the banner shows how long it's run. */
  startedAt: number;
}
let IMPERSONATION: Impersonation | null = null;
export function getImpersonation(): Impersonation | null { return IMPERSONATION; }
/** True while the signed-in admin is acting as someone else. */
export function isImpersonating(): boolean { return IMPERSONATION !== null; }
/** The human actually at the keyboard — the admin, even mid-impersonation.
 *  Anything that ATTRIBUTES an action (audit, logs) must read this, not
 *  `getUser()`, which deliberately returns the person being acted as.
 *  NB: this module must not import lib/audit.ts (audit.ts imports this file);
 *  start/stop entries are written by the call site and the banner. */
export function getRealUser(): CurrentUser { return IMPERSONATION ? IMPERSONATION.realUser : CURRENT_USER; }
/** Re-renders whenever acting-as starts or stops. */
export function useImpersonation(): Impersonation | null {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), []);
  return IMPERSONATION;
}

export function startImpersonation(member: { id?: string; name?: string; email?: string; role?: string; init?: string; color?: string; assignments?: Partial<Record<ProcessId, string>> | null; grants?: string[]; revokes?: string[]; locations?: string[] | null }): boolean {
  if (!member) return false;
  /* A role OR per-process assignments is enough. Requiring `role` meant a
     person whose access lives entirely in `assignments` silently no-oped — the
     caller toasted "Signed in as …" and navigated while the admin was still
     themselves. Callers get a boolean now and can be honest about a refusal. */
  const hasAssignments = !!member.assignments && Object.keys(member.assignments).length > 0;
  if (!member.role && !hasAssignments) return false;
  const realUser = IMPERSONATION ? IMPERSONATION.realUser : CURRENT_USER;
  // Persona-sourced members carry per-process assignments — impersonating the
  // branch manager must resolve her WORKFORCE role, not events-wrap her label.
  const assignments: Partial<Record<ProcessId, string>> =
    hasAssignments ? (member.assignments as Partial<Record<ProcessId, string>>) : { events: member.role as string };
  const role = member.role || Object.values(assignments).find(Boolean) || "";
  const target: CurrentUser = {
    id: member.id, name: member.name || (member.email || "Teammate").split("@")[0],
    email: member.email || "", role, init: member.init || initialsOf(member.name || member.email || "?"),
    color: member.color || "#1F4FA8",
    assignments, grants: member.grants || [], revokes: member.revokes || [],
    permissions: [...effectivePerms({ role, assignments, grants: member.grants, revokes: member.revokes })],
    locations: member.locations || null,
  };
  IMPERSONATION = { realUser, target, startedAt: Date.now() };
  CURRENT_USER = target; notify();
  return true;
}
export function stopImpersonation() {
  if (!IMPERSONATION) return;
  CURRENT_USER = IMPERSONATION.realUser; IMPERSONATION = null; notify();
}
